import {useEffect, useMemo, useRef, useState} from 'react';
import {
   Chart as ChartJS,
   CategoryScale,
   LinearScale,
   PointElement,
   LineElement,
   Title,
   Tooltip as ChartTooltip,
   Legend,
   BarElement
} from 'chart.js';
import {Box, Container, Grid, IconButton, Slider, Typography, Tooltip} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import {Bar, getElementAtEvent, Line} from 'react-chartjs-2';
import SouthIcon from '@mui/icons-material/South';
import NorthIcon from '@mui/icons-material/North';
import {Mark} from '@mui/material/Slider/useSlider.types';

// Registrar los componentes necesarios
ChartJS.register(
   CategoryScale,
   LinearScale,
   PointElement,
   LineElement,
   BarElement,
   Title,
   ChartTooltip,
   Legend
);

type ChartType = 'bar' | 'line';

interface dataChar {
   codigo: string;
   denominacion: string;
   value: number;
   mark?: string;
   subChar?: dataChar[];
}
interface sendInfo {
   total: number;
   filterLevel: Record<number, Set<string>>;
}
interface LineBarDateCharProps {
   titleLevel: Record<number, string>;
   data: dataChar[];
   defaultSlider?: {start: number; end: number};
   minLevel?: number;
   maxLevel?: number;
   codIgnore?: string;
   maxColumns?: Record<number, number>;
   onMultiChartInteraction?: (value: sendInfo) => void;
}

export const LineBarDateMultiChar: React.FC<LineBarDateCharProps> = ({
   titleLevel,
   data,
   defaultSlider = {end: 4, start: 0},
   minLevel = 0,
   maxLevel = 0,
   codIgnore,
   maxColumns = {},
   onMultiChartInteraction = () => {}
}) => {
   /* Toggle */
   const chartBarRef = useRef(null);
   const [chartType, setChartType] = useState<ChartType>('bar');
   /* Slider */
   const [valueSlider, setValueSlider] = useState<[number, number]>([
      defaultSlider.start,
      defaultSlider.end
   ]);
   const [marks, setMarks] = useState<Mark[]>([]);
   const [columns, setColumns] = useState<string[]>([]);
   const [filterLevel, setFilterLevel] = useState<Record<number, Set<string>>>({});
   /* Data */
   const [dataView, setDataView] = useState<dataChar[]>([]);
   const [dataViewChart, setDataViewChart] = useState<dataChar[]>([]);
   const [nivelView, setNivelView] = useState<number>(minLevel);

   const handleChangeLevel = ({
      prevLevel,
      newLevel,
      codColumn
   }: {
      prevLevel: number;
      newLevel: number;
      codColumn?: string;
   }) => {
      let filterPrevLevel: Set<string>;

      const [start, end] = valueSlider;
      const codigosSeleccionados = data.slice(start, end + 1).map(value => value.codigo);
      const sliderFilter: Set<string> = new Set(codigosSeleccionados);
      const copyFilter: Record<number, Set<string>> = {};
      for (const [levelStr, set] of Object.entries(filterLevel)) {
         const level = Number(levelStr);
         if (level < newLevel) {
            copyFilter[level] = set;
         }
      }
      const newFilterLevels: Record<number, Set<string>> = {
         ...copyFilter,
         0: sliderFilter
      };
      if (codColumn) {
         filterPrevLevel = new Set([codColumn]);
         newFilterLevels[prevLevel] = filterPrevLevel;
      }
      let lastDataLevel: dataChar[] = [...data];
      let currLevelProcess = 0;
      while (newLevel > currLevelProcess) {
         const filterNextLevel: Record<string, dataChar[]> = {};
         // Filtrar y juntar niveles
         lastDataLevel.map(currRow => {
            const currFilter = newFilterLevels[currLevelProcess];
            let insertData = false;
            if (currFilter) {
               if (currFilter.has(currRow.codigo)) {
                  insertData = true;
               }
            } else {
               insertData = true;
            }
            if (insertData && currRow.subChar && currRow.subChar.length > 0) {
               if (!filterNextLevel[currRow.codigo]) {
                  filterNextLevel[currRow.codigo] = [];
               }
               filterNextLevel[currRow.codigo] = filterNextLevel[currRow.codigo].concat(
                  currRow.subChar
               );
            }
         });
         // Reducir a únicos
         const acummColumns: Record<string, dataChar> = {};
         Object.values(filterNextLevel).map(value => {
            value.map(currRow => {
               if (codIgnore && codIgnore === currRow.codigo) {
                  return;
               }
               const newValue: dataChar = acummColumns[currRow.codigo] ?? {
                  codigo: currRow.codigo,
                  denominacion: currRow.denominacion,
                  value: 0,
                  subChar: []
               };
               newValue.value += currRow.value;
               newValue.subChar = (newValue.subChar ?? []).concat(currRow.subChar ?? []);
               acummColumns[currRow.codigo] = newValue;
            });
         });
         const newDataLevel: dataChar[] = Object.values(acummColumns);
         lastDataLevel = newDataLevel;
         currLevelProcess += 1;
      }
      setFilterLevel(newFilterLevels);
      setNivelView(newLevel);
      if (maxColumns[newLevel]) {
         const maxNumColumns = maxColumns[newLevel];
         const topValues = lastDataLevel
            .sort((a, b) => b.value - a.value) // Ordena descendente
            .slice(0, maxNumColumns);
         setDataView(topValues);
      } else {
         setDataView(lastDataLevel);
      }
   };

   /* Gráficos */
   const handleSliderChange = (event: Event, newValue: number | number[]) => {
      const [mesInicial, mesFinal] = newValue as [number, number];
      setValueSlider([mesInicial, mesFinal]);
   };

   const handleBarClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
      const chartRef = chartBarRef.current;
      if (!chartRef) return;

      const element = getElementAtEvent(chartRef, event);
      if (element.length > 0) {
         const index = element[0].index;
         const dataRow = dataViewChart[index];
         const codigo = dataRow.codigo;
         if (codigo && nivelView < maxLevel) {
            handleChangeLevel({
               prevLevel: nivelView,
               newLevel: nivelView + 1,
               codColumn: codigo
            });
            if (nivelView === 0) {
               const indexColumn = data.findIndex(currColumn => currColumn.codigo === codigo);
               if (typeof indexColumn === 'number') {
                  setValueSlider([indexColumn, indexColumn]);
               }
            }
         } else {
            if (codigo) {
               const finalLevelFilter: Set<string> = new Set([codigo]);
               const finalFilter = {...filterLevel, [nivelView]: finalLevelFilter};
               const totalValue = dataViewChart.reduce((acc, currRow) => {
                  const sumValue = currRow.codigo === codigo ? currRow.value : 0;
                  return acc + sumValue;
               }, 0);
               const sendInfo: sendInfo = {total: totalValue, filterLevel: finalFilter};
               onMultiChartInteraction(sendInfo);
            }
         }
      }
   };

   const chartData = useMemo(() => {
      let columnsSeleccionados: string[] = dataViewChart.map(value => value.denominacion);
      const dataMap = new Map<number, number>();
      dataViewChart.forEach((item, index) => {
         dataMap.set(index, item.value);
      });
      return {
         labels: columnsSeleccionados,
         datasets: [
            {
               label: titleLevel[nivelView] ?? '',
               data: columnsSeleccionados.map((_, idx) => {
                  return dataMap.get(idx) ?? 0;
               }),
               borderColor: 'rgba(0, 184, 217, 1)',
               backgroundColor: 'rgba(54, 184, 217, 0.2)',
               tension: 0.1,
               borderWidth: 1
            }
         ]
      };
   }, [dataViewChart]);

   const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
         legend: {
            display: false
         },
         tooltip: {
            backgroundColor: '#fff',
            titleColor: '#333',
            bodyColor: '#333',
            borderColor: '#ccc',
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: true,
            padding: 8,
            boxPadding: 6,
            boxWidth: 10,
            boxHeight: 10
         }
      },
      scales: {
         x: {
            ticks: {
               callback: (value: string | number, index: number) => {
                  const currColumn = dataViewChart[index];
                  const label = currColumn.denominacion || String(value);
                  return label.length > 11 ? label.slice(0, 11) + '…' : label;
               }
            }
         }
      }
   };

   useEffect(() => {
      const [start, end] = valueSlider;
      const codigosSeleccionados = data.slice(start, end + 1).map(value => value.codigo);
      const sliderFilter: Set<string> = new Set(codigosSeleccionados);
      const copyFilter: Record<number, Set<string>> = {...filterLevel};
      const newFilterLevels: Record<number, Set<string>> = {
         ...copyFilter,
         0: sliderFilter
      };
      setFilterLevel(newFilterLevels);

      if (nivelView === 0) {
         const columsSlider = dataView.slice(start, end + 1);
         setDataViewChart(columsSlider);
      } else {
         setDataViewChart(dataView);
      }
   }, [dataView]);

   useEffect(() => {
      if (data) {
         const newMarks: Mark[] = data.map((currRow, index) => ({
            value: index,
            label: currRow.mark || currRow.codigo
         }));
         const newColumns: string[] = data.map(currRow => currRow.denominacion);
         setMarks(newMarks);
         setColumns(newColumns);
         setDataView(data);
         setFilterLevel({});
         if (data.length > 0) handleChangeLevel({newLevel: minLevel, prevLevel: minLevel});
      }
   }, [data]);

   useEffect(() => {
      if (data && data.length > 0) {
         handleChangeLevel({newLevel: nivelView, prevLevel: nivelView});
      }
   }, [valueSlider]);

   useEffect(() => {
      const totalValue = dataViewChart.reduce((acc, currRow) => acc + currRow.value, 0);
      const finalFilter = {...filterLevel};
      if (maxColumns[nivelView]) {
         const viewColumns = dataViewChart.map(currRow => currRow.codigo);
         const maxLevelFilter: Set<string> = new Set(viewColumns);
         finalFilter[nivelView] = maxLevelFilter;
      }
      const sendInfo: sendInfo = {total: totalValue, filterLevel: finalFilter};
      onMultiChartInteraction(sendInfo);
   }, [dataViewChart]);

   return (
      <Container sx={{height: '100%', maxHeight: '80vh'}}>
         <Grid item container>
            <Grid item xs={12}>
               <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  {/* Iconos de tipo de gráfico (izquierda) */}
                  <Box display="flex" gap={1}>
                     <Tooltip title="Gráfico de Barras" placement="top" arrow>
                        <IconButton
                           onClick={() => setChartType('bar')}
                           sx={{
                              p: 0.5,
                              color: chartType === 'bar' ? 'primary.main' : 'text.disabled',
                              '&:hover': {
                                 backgroundColor: 'primary.light',
                                 color: 'primary.main'
                              }
                           }}
                        >
                           <BarChartIcon fontSize="large" />
                        </IconButton>
                     </Tooltip>
                     <Tooltip title="Gráfico Lineal" placement="top" arrow>
                        <IconButton
                           onClick={() => setChartType('line')}
                           sx={{
                              p: 0.5,
                              color: chartType === 'line' ? 'primary.main' : 'text.disabled',
                              '&:hover': {
                                 backgroundColor: 'primary.light',
                                 color: 'primary.main'
                              }
                           }}
                        >
                           <ShowChartIcon fontSize="large" />
                        </IconButton>
                     </Tooltip>
                  </Box>

                  {/* Título centrado */}
                  <Typography variant="h5" mt={0.5} textAlign="center" lineHeight={1.1}>
                     {titleLevel[nivelView] ?? ''}
                  </Typography>

                  {/* Iconos de orden (derecha) */}
                  <Box display="flex" gap={1}>
                     <Tooltip
                        title={nivelView <= 0 ? '' : 'Gráfico Anterior'}
                        placement="top"
                        arrow
                     >
                        <span>
                           <IconButton
                              onClick={() => {
                                 handleChangeLevel({
                                    newLevel: nivelView - 1,
                                    prevLevel: nivelView
                                 });
                              }}
                              sx={{
                                 p: 0.5,
                                 '&:hover': {
                                    backgroundColor: 'primary.light',
                                    color: 'primary.main'
                                 }
                              }}
                              disabled={nivelView <= minLevel}
                           >
                              <NorthIcon fontSize="medium" />
                           </IconButton>
                        </span>
                     </Tooltip>

                     <Tooltip
                        title={nivelView >= maxLevel ? '' : 'Siguiente Gráfico'}
                        placement="top"
                        arrow
                     >
                        <span>
                           <IconButton
                              onClick={() => {
                                 handleChangeLevel({
                                    newLevel: nivelView + 1,
                                    prevLevel: nivelView
                                 });
                              }}
                              sx={{
                                 p: 0.5,
                                 '&:hover': {
                                    backgroundColor: 'primary.light',
                                    color: 'primary.main'
                                 }
                              }}
                              disabled={nivelView >= maxLevel}
                           >
                              <SouthIcon fontSize="medium" />
                           </IconButton>
                        </span>
                     </Tooltip>
                  </Box>
               </Box>
            </Grid>
         </Grid>
         <Grid item sm={12} sx={{height: 'calc(100% - 100px)'}}>
            {chartType === 'line' ? (
               <Line options={options} data={chartData} />
            ) : (
               <Bar ref={chartBarRef} options={options} data={chartData} onClick={handleBarClick} />
            )}
         </Grid>
         <Grid item sm={12}>
            <div style={{width: '90%', margin: 'auto'}}>
               <Slider
                  value={valueSlider}
                  onChange={handleSliderChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={columns.length - 1}
                  marks={marks}
                  step={1}
                  valueLabelFormat={index => columns[index]}
                  style={{fontSize: '5px important!'}}
               />
            </div>
         </Grid>
      </Container>
   );
};
